import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import testImg from "../../../../Assets/img/test-img.png";
import HTMLParser from "../../../Components/HTMLParser";
import Loader from "../../../Components/Loader/Loader";
import {
  errorMessage,
  getSchemeSets,
  getScoringScheme,
} from "../../../../Features/LKWBTestApi/LKWBTestApi";

const stripHtml = (html) =>
  typeof html === "string"
    ? html
        .replace(/<\/p>\s*<p>/gi, " · ")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim()
    : "";

const show = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const Row2 = ({ label, value }) => (
  <p className="lkwb-row">
    <span>{label}</span>
    <strong>{value}</strong>
  </p>
);

// Scoring scheme rules, shown in the right sidebar.
const SchemeRules = ({ scheme }) => (
  <div className="sticky lkwb-rules">
    <div className="eachdashboard-detail">
      <h3 className="eachdashboard-title">Rules</h3>
      {scheme.description && <HTMLParser>{scheme.description}</HTMLParser>}
      {scheme.mode_label && <Row2 label="Mode" value={scheme.mode_label} />}
      <Row2 label="Total Questions" value={show(scheme.total_questions)} />
      <Row2 label="Total Marks" value={show(scheme.total_marks)} />
      {scheme.objective_marks != null && (
        <Row2 label="Objective Marks" value={scheme.objective_marks} />
      )}
      {scheme.writing_marks != null && (
        <Row2 label="Writing Marks" value={scheme.writing_marks} />
      )}
      {scheme.pass_marks != null && (
        <Row2 label="Pass Marks" value={scheme.pass_marks} />
      )}
    </div>
    {Array.isArray(scheme.rules) && scheme.rules.length > 0 && (
      <div className="eachdashboard-detail">
        <h3 className="eachdashboard-title">Marking</h3>
        {scheme.rules.map((rule) => (
          <React.Fragment key={rule.id}>
            <p className="lkwb-rule-label">{rule.label}</p>
            <Row2
              label={`${rule.question_count} × ${rule.marks}`}
              value={`${rule.total_marks} Marks`}
            />
          </React.Fragment>
        ))}
      </div>
    )}
  </div>
);

const LKWBTestDetail = () => {
  const { id } = useParams();
  const token = useSelector((state) => state.auth.currentUser.token);
  const [scheme, setScheme] = useState(null);
  const [sets, setSets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [setsError, setSetsError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.allSettled([
      getScoringScheme(id, token),
      getSchemeSets(id, token),
    ]).then(([schemeRes, setsRes]) => {
      if (cancelled) return;

      if (schemeRes.status === "fulfilled") {
        setScheme(schemeRes.value);
        setError("");
      } else {
        setError(errorMessage(schemeRes.reason, "Unable to load this test."));
      }

      if (setsRes.status === "fulfilled") {
        setSets(setsRes.value?.sets || []);
        // Fall back to the scheme summary returned with the sets.
        if (schemeRes.status !== "fulfilled" && setsRes.value?.scheme) {
          setScheme(setsRes.value.scheme);
          setError("");
        }
        setSetsError("");
      } else {
        setSetsError(errorMessage(setsRes.reason, "Unable to load sets."));
      }

      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  if (isLoading) {
    return (
      <div className="loading__mode bigLoading">
        <Loader />
      </div>
    );
  }

  return (
    <Row>
      <Col md={9}>
        <div className="dashboard-contents dashboard-test">
          <Link to="/dashboard/lkwb-test" className="btn btn-small">
            Go Back
          </Link>

          {error || !scheme ? (
            <div className="youhavenot-container mt-3">
              <p>{error || "Test not found."}</p>
            </div>
          ) : (
            <div className="dashboard-contentshead">
              <img src={testImg} alt="" />
              <div className="packageName">
                <h3 className="eachdashboard-title">{scheme.title}</h3>
                <p className="lkwb-meta">
                  {[
                    scheme.mode_label,
                    scheme.total_questions != null &&
                      `${scheme.total_questions} Questions`,
                    scheme.total_marks != null && `${scheme.total_marks} Marks`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </div>
          )}

          <div className="eachdashboard-detail">
            <h3 className="eachdashboard-title lkwb-sets-title">Sets</h3>
            {setsError ? (
              <div className="youhavenot-container">
                <p>{setsError}</p>
              </div>
            ) : sets.length === 0 ? (
              <div className="youhavenot-container">
                <p>No sets available for this test.</p>
              </div>
            ) : (
              <div className="setstitem-container lkwb-list">
                {sets.map((set) => {
                  // Bought sets open the test; others go through the buy step first.
                  const link = `/dashboard/lkwb-test/${id}/sets/${set.id}${
                    set.status ? "" : "/buy"
                  }`;
                  const action = set.status
                    ? "Test Now"
                    : !set.price || set.canAccessSet
                    ? "Claim Now"
                    : "Buy Now";
                  return (
                    <div
                      key={set.id}
                      className={`normalsetsitem setsitem-container lkwb-card ${
                        set.status ? "item-bought" : "item-notbought"
                      } ${set.isNew ? "newTest" : ""}`}
                    >
                      <div className="new__test">New</div>
                      <div className="setsitem-header">
                        <div className="pdficon">
                          <img src={testImg} alt="" />
                          <div className="sets-content">
                            <div>
                              <Link to={link}>
                                <h5>{set.title}</h5>
                              </Link>
                              {set.description && (
                                <p className="lkwb-meta">
                                  {stripHtml(set.description)}
                                </p>
                              )}
                            </div>
                            <div className="text-end lkwb-card-actions">
                              <Link
                                to={`/dashboard/lkwb-test/${id}/sets/${set.id}/results`}
                                className="btn btn-small btn-outline"
                              >
                                Results
                              </Link>
                              <Link to={link} className="btn btn-small">
                                {action}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Col>
      <Col md={3} className="rightsidebar">
        {scheme && <SchemeRules scheme={scheme} />}
      </Col>
    </Row>
  );
};

export default LKWBTestDetail;
